import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { CircleAlert, Layers, Lock, Trash2 } from 'lucide-react';
import { useAccessToken } from '@/auth/useAccessToken';
import { useMe } from '@/features/me/useMe';
import { ticketsApi } from '@/features/tickets/ticketsApi';
import { useQueueEvents } from '@/features/tickets/useQueueEvents';
import { StatusBadge, STATUS_LABELS } from '@/features/tickets/StatusBadge';
import type { QueueTicketListItem, TicketStatus } from '@/features/tickets/schema';
import { ApiError } from '@/lib/apiClient';

const STATUS_ORDER: TicketStatus[] = [
  'Pending',
  'InProgress',
  'WaitingOnSomeone',
  'PendingSoftwareRelease',
  'Completed'
];

/**
 * /queue — the IT view of ALL tickets (RequireIT on the API). End users don't
 * see the nav link, and a direct hit returns 403 (handled below). Filters by
 * status + "assigned to me" drive the query key so changing them refetches.
 */
export function Queue() {
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();
  const me = useMe();
  const isAdmin = me.data?.role === 'ITAdmin';
  const [status, setStatus] = useState<TicketStatus | ''>('');
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const tickets = useQuery({
    queryKey: ['tickets', 'queue', status, assignedToMe],
    queryFn: ({ signal }) =>
      ticketsApi.listQueue({ status: status || undefined, assignedToMe }, getAccessToken, signal)
  });

  // Live updates: any ticket change / creation refreshes the queue.
  useQueueEvents();

  const bulkDelete = useMutation({
    mutationFn: () => ticketsApi.bulkDeleteTickets([...selected], getAccessToken),
    onSuccess: () => {
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'queue'] });
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'mine'] });
    }
  });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const forbidden = (tickets.error as ApiError)?.status === 403;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
            <Layers className="w-6 h-6 text-primary" /> Queue
          </h1>
          <p className="text-sm text-muted-foreground mt-1">All tickets across the organization.</p>
        </div>
      </header>

      {!forbidden && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as TicketStatus | '')}
            className="rounded-md border bg-card px-3 py-2 text-sm"
          >
            <option value="">All statuses</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={assignedToMe}
              onChange={(e) => setAssignedToMe(e.target.checked)}
              className="rounded border-input h-4 w-4"
            />
            Assigned to me
          </label>
          {tickets.data && (
            <span className="ml-auto text-xs text-muted-foreground">{tickets.data.length} ticket{tickets.data.length === 1 ? '' : 's'}</span>
          )}
        </div>
      )}

      {isAdmin && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 sm:gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 sm:px-4 py-2 text-sm">
          <span>{selected.size} selected</span>
          <button
            type="button"
            disabled={bulkDelete.isPending}
            onClick={() => {
              if (confirm(`Permanently delete ${selected.size} ticket${selected.size === 1 ? '' : 's'} and their threads? This can't be undone.`)) {
                bulkDelete.mutate();
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-1.5 text-destructive hover:bg-destructive/10 disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> {bulkDelete.isPending ? 'Deleting…' : 'Delete selected'}
          </button>
          <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-muted-foreground hover:text-foreground">
            Clear
          </button>
        </div>
      )}

      {tickets.isPending && <ListSkeleton />}

      {forbidden && (
        <div className="rounded-xl border border-dashed bg-card p-10 text-center">
          <Lock className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <div className="text-base font-medium">IT access required</div>
          <p className="text-sm text-muted-foreground mt-1.5">The ticket queue is only available to IT staff.</p>
        </div>
      )}

      {tickets.isError && !forbidden && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-start gap-2">
          <CircleAlert className="w-4 h-4 mt-0.5" />
          <span>Couldn't load the queue: {(tickets.error as Error).message}</span>
        </div>
      )}

      {tickets.data && tickets.data.length === 0 && (
        <div className="rounded-xl border border-dashed bg-card p-10 text-center text-sm text-muted-foreground">
          No tickets match these filters.
        </div>
      )}

      {tickets.data && tickets.data.length > 0 && (
        <QueueTable
          tickets={tickets.data}
          selectable={isAdmin}
          selected={selected}
          onToggle={toggle}
          onToggleAll={(ids, all) => setSelected(all ? new Set() : new Set(ids))}
        />
      )}
    </div>
  );
}

function QueueTable({
  tickets,
  selectable,
  selected,
  onToggle,
  onToggleAll
}: {
  tickets: QueueTicketListItem[];
  selectable: boolean;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (ids: string[], allSelected: boolean) => void;
}) {
  const allSelected = tickets.length > 0 && tickets.every((t) => selected.has(t.id));
  return (
    <>
      {/* Phones: stacked cards. Tapping the card navigates; tapping the
          checkbox toggles selection (we stopPropagation so it doesn't navigate). */}
      <div className="md:hidden space-y-2">
        {selectable && (
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground select-none px-1">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => onToggleAll(tickets.map((t) => t.id), allSelected)}
              className="rounded border-input h-4 w-4"
              aria-label="Select all"
            />
            Select all
          </label>
        )}
        <ul className="space-y-2">
          {tickets.map((t) => (
            <li key={t.id}>
              <Link
                to={`/tickets/${t.ticketNumber}`}
                className="block rounded-xl border bg-card p-4 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-start gap-3">
                  {selectable && (
                    <label
                      onClick={(e) => e.stopPropagation()}
                      className="pt-0.5 cursor-pointer"
                      aria-label={`Select ticket ${t.ticketNumber}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(t.id)}
                        onChange={() => onToggle(t.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-input h-4 w-4"
                      />
                    </label>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs text-muted-foreground">#{t.ticketNumber}</span>
                      <StatusBadge status={t.status} />
                    </div>
                    <div className="mt-1 font-medium leading-snug">{t.subject}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-muted">{t.category}</span>
                      <span>· {t.requesterDisplayName}</span>
                      <span>· {formatDistanceToNow(new Date(t.updatedUtc), { addSuffix: true })}</span>
                      <span>· {t.assigneeDisplayName ?? <span className="italic">Unassigned</span>}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* md+: the full table. */}
      <div className="hidden md:block rounded-xl border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
            <tr>
              {selectable && (
                <th className="px-3 py-3 font-medium w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => onToggleAll(tickets.map((t) => t.id), allSelected)}
                    className="rounded border-input"
                    aria-label="Select all"
                  />
                </th>
              )}
              <th className="px-5 py-3 font-medium">#</th>
              <th className="px-3 py-3 font-medium">Subject</th>
              <th className="px-3 py-3 font-medium">Requester</th>
              <th className="px-3 py-3 font-medium">Category</th>
              <th className="px-3 py-3 font-medium">Status</th>
              <th className="px-3 py-3 font-medium">Last update</th>
              <th className="px-3 py-3 font-medium">Assigned</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tickets.map((t) => (
              <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                {selectable && (
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => onToggle(t.id)}
                      className="rounded border-input"
                      aria-label={`Select ticket ${t.ticketNumber}`}
                    />
                  </td>
                )}
                <td className="px-5 py-3 font-mono text-muted-foreground">
                  <Link to={`/tickets/${t.ticketNumber}`} className="hover:underline">#{t.ticketNumber}</Link>
                </td>
                <td className="px-3 py-3 font-medium">
                  <Link to={`/tickets/${t.ticketNumber}`} className="hover:underline block">{t.subject}</Link>
                </td>
                <td className="px-3 py-3 text-muted-foreground">{t.requesterDisplayName}</td>
                <td className="px-3 py-3">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-muted text-xs">{t.category}</span>
                </td>
                <td className="px-3 py-3"><StatusBadge status={t.status} /></td>
                <td className="px-3 py-3 text-muted-foreground">{formatDistanceToNow(new Date(t.updatedUtc), { addSuffix: true })}</td>
                <td className="px-3 py-3">{t.assigneeDisplayName ?? <span className="text-muted-foreground italic">Unassigned</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ListSkeleton() {
  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="px-5 py-3 bg-muted/30">
        <div className="h-3 w-24 bg-muted rounded animate-pulse" />
      </div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="px-5 py-4 border-t flex items-center gap-3">
          <div className="h-3 w-12 bg-muted rounded animate-pulse" />
          <div className="h-3 flex-1 bg-muted rounded animate-pulse" />
          <div className="h-3 w-20 bg-muted rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}
